import _get from 'lodash/get';
import _toString from 'lodash/toString';
import _each from 'lodash/each';
import _set from 'lodash/set';
import _merge from 'lodash/merge';
import {
    MediaPlayerEvents,
    FeatureDetection,
} from 'mlb-media-player';
import Device from '../../utils/device';
import BaseAdvertisingPlugin from '../base-advertising-plugin';
import AdvertisingPluginEvents from '../advertising-plugin-events';
import ScriptLoader from '../../utils/script-loader';
import DfpRequestBuilder from './dfp-request-builder';
import MoatTracking from './moat-tracking';

/**
 *
 * @module DfpPlugin
 * @desc
 * @class DfpPlugin
 *
 */

class DfpPlugin extends BaseAdvertisingPlugin {

    constructor(config, obsvervationCenter) {

        super(config, obsvervationCenter);

        this.googleIMA = undefined;

        this.scriptUrl = _get(this.config, 'dfp.adManager', 'https://imasdk.googleapis.com/js/sdkloader/ima3.js');

        this.pluginSubject = this.observationCenter.pluginSubject;

        this.videoAdContainerId = _get(this.config, 'videoAdContainerId', 'ad-container');

        this.adContainer = undefined;

        this.controls = _get(this.config, 'controls', false);

        this.autoplay = _get(this.config, 'autoplay', true);

        this.muted = _get(this.config, 'muted', true);

        this.initVolume = this.muted ? 0 : 1;

        this.playsInline = _get(this.config, 'playsinline', true);

        this.url = _get(this.config, 'dfp.url'); //check defaul value in config?

        this.output = _get(this.config, 'dfp.output');

        this.description_url = _get(this.config, 'dfp.description_url');

        this.cmsid = _get(this.config, 'dfp.cmsid');

        this.ad_rule = _get(this.config, 'dfp.ad_rule');

        this.pp = _get(this.config, 'dfp.pp');

        this.ciu_szs = _get(this.config, 'dfp.ciu_szs');

        this.gdfp_req = _get(this.config, 'dfp.gdfp_req');

        this.unviewed_position_start = _get(this.config, 'dfp.unviewed_position_start');

        // make this 3 values based on team page or regular vpp

        this.iu = _get(this.config, 'dfp.vpp.iu');

        this.sz = _get(this.config, 'dfp.vpp.sz');

        this.cust_params = _get(this.config, 'dfp.cust_params');

        this.contentResumeCalled = false;

        this.deviceManager = new Device();

        this.onAdsManagerLoaded = this.onAdsManagerLoaded.bind(this);
        this.onAdError = this.onAdError.bind(this);

        this.initAdContainer();

    }

    /**
     *
     * @desc Initialize and create the ad container and video node. Also attaches the video node to the DOM
     * as a child of the ad container. The video node is prepended to the children of the adContainer
     *
     */
    initAdContainer() {

        this.adContainer = document.getElementById(this.videoAdContainerId);

        if (!this.adContainer) {

            throw new Error(`An element with the id ${this.videoAdContainerId} is required for ad playback`);

        }

    }

    /**
     * Recursively traverses up the DOM finding the nearest parent with a width and a height
     *
     * @param leaf {DOMElement} starting point
     *
     * @return {Object} Object containing `width` and `height` properties
     */
    findParentSize(baseElement) {

        const parent = baseElement.parentNode;
        let width = 640;
        let height = 360;

        if (parent) {

            width = _get(parent, 'clientWidth');
            height = _get(parent, 'clientHeight');

        }

        if (width > 0 && height > 0) {

            return {
                width,
                height,
            };

        }

        return this.findParentSize(parent);

    }

    /**
     *
     * @desc Subscribe to the ObservationCenter messages
     *
     */
    subscribeToMessages() {

        const PlayerEvents = MediaPlayerEvents[this.mediaPlayer.player.playerTypeConfig.playerType];
        const MediaEvents = MediaPlayerEvents['MEDIA_PLAYER'];

        // Player subscriptions
        const playerSubscription = this.observationCenter.playerSubject.subscribe((data) => {

            switch (data.type) {

                case PlayerEvents.VOLUME_CHANGE: {

                    const volume = _get(data, 'event.target.volume');
                    const muted = _get(data, 'event.target.muted');
                    this.onVolumeChanged(volume, muted);
                    break;

                }

                case MediaEvents.PLAYER_RESIZED: {

                    const newSize = _get(data, 'data');
                    this.setSize(newSize.width, newSize.height);
                    break;

                }

            }

        });

        // Plugin subscriptions

        const pluginSubscription = this.observationCenter.pluginSubject.subscribe((data) => {

            switch (data.type) {

                case AdvertisingPluginEvents.AD_START: {

                    this.mediaPlayer.setIsAdPlaying(true);
                    this.adContainer.style.display = '';
                    break;

                }

                case AdvertisingPluginEvents.AD_COMPLETE: {

                    this.mediaPlayer.setIsAdPlaying(false);
                    this.adContainer.style.display = 'none';
                    break;

                }

                case AdvertisingPluginEvents.AD_ERROR:
                case AdvertisingPluginEvents.AD_PLUGIN_ERROR:
                case AdvertisingPluginEvents.AD_BLOCK_DETECTED: {

                    this.pluginDidError = true;
                    this.mediaPlayer.setIsAdPlaying(false);
                    this.adContainer.style.display = 'none';
                    this.destroy();
                    break;

                }

            }

        });

        this.subscriptions.push(playerSubscription);
        this.subscriptions.push(pluginSubscription);

    }

    /**
     *
     * @desc Unsubscribe from the event stream
     * @private
     *
     */
    unsubscribe() {

        this.subscriptions.forEach((subscription) => {

            subscription.unsubscribe();

        });

    }

    setMediaPlayer(mediaPlayer) {

        /* If we have a mediaPlayer, we need to unsubscribe from the event streams
            because we're going to resubscribe shorlty for the new player */
        if (this.mediaPlayer) {

            this.unsubscribe();

        }

        // Set so subscribeToMessages has a reference
        this.mediaPlayer = mediaPlayer;

        // init the plugin
        return this.init().then(() => {

            this.subscribeToMessages();

            // Let the super know (this also calls pluginReady())
            super.setMediaPlayer(mediaPlayer);

        });

    }

    /**
     *
     * @desc Initialize the plugin.
     *
     */
    init(scriptUrl) {

        return Promise
            .resolve(_get(window, 'window.google.ima'))
            .then((googleIMA) => {

                if (!googleIMA) {

                    return ScriptLoader.getAPI(this.scriptUrl, 'window.google.ima');

                }
                //console.log('load ad manager');
                return googleIMA;

            })
            .then((googleIMA) => {

                //console.log('ad manager loaded');
                if (!this.googleIMA) {

                    this.googleIMA = googleIMA;

                }

                this.onScriptLoaded();

            })
            .catch((error) => {

                console.log('ad manager error', error);

                this.adBlockedError = true;

                this.destroy();

                // Dispatch error message
                this.pluginSubject.next({
                    type: AdvertisingPluginEvents.AD_PLUGIN_ERROR,
                    error,
                });

            });

    }

    /**
     *
     * @access private
     * @desc Called when the DFPPlugin has been successfully loaded.
     * Dispatches the AD_PLUGIN_LOADED event
     *
     */
    onScriptLoaded() {

        this.ready = true;
        // log.call(this, 'onScriptLoaded');

        // Send AD_PLUGIN_LOADED message
        this.pluginSubject.next({ type: AdvertisingPluginEvents.AD_PLUGIN_LOADED });

    }

    onVolumeChanged(volume, muted) {

        const adsManager = this.adsManager;

        if (adsManager) {

            adsManager.setVolume(muted ? 0 : volume);

        }

    }


    initializeForAutoPlayPermission(adVideoElement, adContainer) {

        this.clearAdsLoader();
        this.initializeAdContainer(adContainer);

    }

    /**
     *
     * @access private
     * @desc Cleans up and deletes the adLoader, so it can be created within a userEvent
     *
     */
    clearAdsLoader() {

        if (this.adsLoader) {

            this.adsLoader.removeEventListener(
                this.googleIMA.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
                this.onAdsManagerLoaded);
            this.adsLoader.removeEventListener(
                this.googleIMA.AdErrorEvent.Type.AD_ERROR,
                this.onAdError);

            delete this.adsLoader;
            delete this.adBlockedError;

        }

    }

    initializeAdContainer(adContainer) {

        const { width, height } = this.findParentSize(this.adContainer);
        this.setSize(width, height);

        if (!this.adsLoader) {

            const adDisplayContainer = new this.googleIMA.AdDisplayContainer(adContainer);
            adDisplayContainer.initialize();

            // Create ads loader.
            this.adsLoader = new this.googleIMA.AdsLoader(adDisplayContainer);

            // Listen and respond to ads loaded and error events.
            this.adsLoader.addEventListener(this.googleIMA.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, this.onAdsManagerLoaded, false);
            this.adsLoader.addEventListener(this.googleIMA.AdErrorEvent.Type.AD_ERROR, this.onAdError, false);


        }

    }

    /**
     *
     * @desc Set the volume on the adManager for the ads
     * @param {double} newVolume Value between 0 (no volume) and 1 (loudest)
     *
     */
    setVolume(newVolume) {

        this.initVolume = newVolume;

        if (this.adsManager) {

            this.mediaPlayer.setVolume(this.initVolume);
            this.adsManager.setVolume(this.initVolume);

        }

    }


    // ========================================
    // Ad requests
    // ========================================

    requestAd(clip) {

        if (this.ready) {

            this.mediaPlayer.pause();

            this.clip = clip;
            this.currentAdDuration = 0;
            this.adPodDuration = 0;
            this.elapsedAdsInPodTime = 0;

            this.initializeAdContainer(this.adContainer);

            this.startAdsRequest();

        } else if (!this.adBlockedError) {

            ////log.call(this, 'requestAdQueued');

            this.requestAdTimeoutID = setTimeout(() => {

                this.requestAd(clip);

            }, 500);

        }

    }

    startAdsRequest(cuePointVO) {

        // Request video ads.
        const adsRequest = new this.googleIMA.AdsRequest();

        const dfpConfig = _get(this.config, 'dfp', {});
        const request = DfpRequestBuilder.build(dfpConfig, this.clip, cuePointVO);

        adsRequest.adTagUrl = request;

        // Specify the linear and nonlinear slot sizes. This helps the SDK to
        // select the correct creative if multiple are returned.
        adsRequest.linearAdSlotWidth = 640;
        adsRequest.linearAdSlotHeight = 400;

        adsRequest.nonLinearAdSlotWidth = 640;
        adsRequest.nonLinearAdSlotHeight = 150;

        FeatureDetection.testHTML5Autoplay()
            .then((result) => {

                const stateModel = _get(this.mediaPlayer, 'player.stateModel');
                const { volume } = stateModel;
                this.autoPlayPolicy = result;

                if (result.regular) {

                    adsRequest.setAdWillAutoPlay(true);
                    adsRequest.setAdWillPlayMuted(volume.muted);

                    this.setVolume(volume.muted ? 0 : volume.value);

                } else if (result.muted) {

                    adsRequest.setAdWillAutoPlay(true);
                    adsRequest.setAdWillPlayMuted(true);

                    this.setVolume(0);

                } else {

                    console.error('Ad request error, we can\'t determine autoplay, we\'ll default muted');

                    adsRequest.setAdWillAutoPlay(true);
                    adsRequest.setAdWillPlayMuted(true);

                }

                this.adsLoader.requestAds(adsRequest);

            })
            .catch((error) => {

                console.error('Ad request error', error);

            });

    }


    // ===============================================
    // DFP/Player API
    // ===============================================

    /**
     *
     * @access private
     * @param {HTMLElement} adContainer - the container that the video node lives
     * @param {Object} event
     * @desc start the ad playing. Copied from the docs at
     * https://developers.google.com/interactive-media-ads/docs/sdks/html5/quickstart
     *
     */
    onAdsManagerLoaded(event) {

        // Get the ads manager.
        const adsRenderingSettings = new this.googleIMA.AdsRenderingSettings();
        adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;

        // 1st param should be set to the content video element.
        this.adsManager = event.getAdsManager(this.mediaPlayer.player.videoEl, adsRenderingSettings);

        // Add listeners to the required events.
        this.adsManager.addEventListener(this.googleIMA.AdErrorEvent.Type.AD_ERROR, this.onAdError);
        this.adsManager.addEventListener(this.googleIMA.AdEvent.Type.CONTENT_PAUSE_REQUESTED, this.onContentPauseRequested.bind(this));
        this.adsManager.addEventListener(this.googleIMA.AdEvent.Type.CONTENT_RESUME_REQUESTED, this.onContentResumeRequested.bind(this));
        this.adsManager.addEventListener(this.googleIMA.AdEvent.Type.ALL_ADS_COMPLETED, this.onAdEvent.bind(this));

        // Listen to any additional events, if necessary.
        this.adsManager.addEventListener(this.googleIMA.AdEvent.Type.LOADED, this.onAdEvent.bind(this));
        this.adsManager.addEventListener(this.googleIMA.AdEvent.Type.STARTED, this.onAdEvent.bind(this));
        this.adsManager.addEventListener(this.googleIMA.AdEvent.Type.COMPLETE, this.onAdEvent.bind(this));
        this.adsManager.addEventListener(this.googleIMA.AdEvent.Type.AD_PROGRESS, this.onAdEvent.bind(this));


        try {

            // Initialize the ads manager. Ad rules playlist will start at this time.
            let {
                width,
                height,
            } = _get(this.config, 'dfp', {});

            if(!(width && height)) {

                ({
                    width,
                    height,
                } = this.findParentSize(this.adContainer));

            }

            // const muted = _get(this.mediaPlayer, 'player.stateModel.volume.muted', true);
            // const playerVolume = _get(this.mediaPlayer, 'player.stateModel.volume.value', 0);
            this.adsManager.init(width, height, this.googleIMA.ViewMode.NORMAL);

            // Call start to show ads. Single video and overlay ads will
            // start at this time; this call will be ignored for ad rules, as ad rules
            // ads start when the adsManager is initialized.
            this.adsManager.start();

        } catch (adError) {

            // An error may be thrown if there was a problem with the VAST response.
            console.log('ERROR', adError);

        }

    }

    /**
     *
     * @access private
     * @param {Object} adEvent
     * @desc common handler for all ad events {LOADED, STARTED, COMPLETE}
     *
     */
    onAdEvent(adEvent) {

        // Retrieve the ad from the event. Some events (e.g. ALL_ADS_COMPLETED)
        // don't have ad object associated.

        const ad = adEvent.getAd();
        //log.call(this, 'onAdEvent: [', adEvent.type, '] isLinear:', ad.isLinear());
        switch (adEvent.type) {

            case this.googleIMA.AdEvent.Type.LOADED:
                // This is the first event sent for an ad - it is possible to
                // determine whether the ad is a video ad or an overlay.

                /** Init Moat ad tracking */
                this.trackMoat();
                this.contentResumeCalled = false;

                if (!ad.isLinear()) {
                    // Position AdDisplayContainer correctly for overlay.
                    // Use ad.width and ad.height.
                    // [MIGHT NEED TO REVIEW - DS] videoContent.play();

                }
                //log.call(this, 'AD_LOADED');
                // TODO: Replace with subject...
                //this.dispatcher.publish(PaidInterruptionEvents.AD_LOADED, adEvent.o);
                this.pluginSubject.next({
                    type: AdvertisingPluginEvents.AD_LOADED,
                    adEvent,
                });
                break;

            case this.googleIMA.AdEvent.Type.STARTED:
                // This event indicates the ad has started - the video player
                // can adjust the UI, for example display a pause button and
                // remaining time.
                this.contentResumeCalled = false;

                if ([
                    'phone',
                    'tablet',
                ].includes(this.deviceManager.formFactor)) {

                    this.observationCenter.controlsSubject.next({
                        type: 'HIDE_CONTROLS',
                    });

                }

                if (ad.isLinear()) {

                    // For a linear ad, a timer can be started to poll for
                    // the remaining time.
                    const self = this;
                    this.elapsedAdsInPodTime += this.currentAdDuration;
                    this.currentAdDuration = ad.getDuration();
                    //log.call(this, 'AD_STARTED', 'currentAdDuration:', this.currentAdDuration, 'elapsedAdsInPodTime', this.elapsedAdsInPodTime);
                    // this.intervalTimer = setInterval(this.updateRemainingTimer.bind(self), 300); // every 300ms
                    this.updateRemainingTimer();

                }
                break;

            case this.googleIMA.AdEvent.Type.COMPLETE:
                // This event indicates the ad has finished - the video player
                // can perform appropriate UI actions, such as removing the timer for
                // remaining time detection.

                this.observationCenter.controlsSubject.next({
                    type: 'SHOW_CONTROLS',
                });

                if (ad.isLinear()) {

                    //log.call(this, 'AD_COMPLETE');
                    //clearInterval(this.intervalTimer);

                }
                break;

            case this.googleIMA.AdEvent.Type.ALL_ADS_COMPLETED:

                this.observationCenter.controlsSubject.next({
                    type: 'SHOW_CONTROLS',
                });

                const preRollAdsEnabled = _get(this.config, 'preRollAdsEnabled', false);
                const midRollAdsEnabled = _get(this.config, 'midRollAdsEnabled', false);
                if (preRollAdsEnabled || midRollAdsEnabled) {

                    // TODO: replace with subject
                    // TODO: Replace with subject...//
                    //this.dispatcher.publish(PaidInterruptionEvents.ALL_ADS_COMPLETED, this.config);
                    this.pluginSubject.next({
                        type: AdvertisingPluginEvents.ALL_ADS_COMPLETED,
                        config: this.config,
                    });

                }

                if (ad.isLinear()) {

                    ////log.call(this, 'ALL_ADS_COMPLETED');
                    // Sometimes 'complete' event is not triggered and we only receive this event
                    // (noticed it when there is only one ad)
                    //clearInterval(this.intervalTimer);

                }

                break;

            case this.googleIMA.AdEvent.Type.AD_PROGRESS: {

                this.updateRemainingTimer();
                break;

            }

        }

    }

    /**
     *
     * @access private
     * @param {Object} adErrorEvent
     * @desc publish AD_ERROR and destroy the adsManager session
     *
     */
    onAdError(adErrorEvent) {

        // Handle the error logging.

        const AD_AUTOPLAY_BLOCKED_ERROR_CODE = 1205;
        //log.call(this, adErrorEvent.getError());
        let innerErrorCode;
        const error = adErrorEvent.getError();
        const innerError = error.getInnerError();
        if (innerError) {

            innerErrorCode = innerError.getErrorCode();

        }

        if (innerErrorCode === AD_AUTOPLAY_BLOCKED_ERROR_CODE) {

            this.adBlockedError = true;
            // TODO: Replace with subject...
            //this.dispatcher.publish(PaidInterruptionEvents.AD_AUTOPLAY_BLOCKED);
            this.pluginSubject.next({ type: AdvertisingPluginEvents.AD_AUTOPLAY_BLOCKED });

        } else {

            // TODO: Replace with subject...
            //this.dispatcher.publish(PaidInterruptionEvents.AD_ERROR, adErrorEvent);
            this.pluginSubject.next({
                type: AdvertisingPluginEvents.AD_ERROR,
                error: adErrorEvent,
            });

            if (this.adsManager) {

                this.destroy();

            }

        }

    }

    /**
     *
     * @access private
     * @param {Object} adEvent
     * @desc event handler that the content has paused and that the AD_START is fired
     * Use this method to setup UI for showing ads (e.g.
     * display ad timer countdown, disable seeking etc.)
     *
     */
    onContentPauseRequested(adEvent) {

        const duration = this.adsManager.getRemainingTime();
        const ad = adEvent.getAd();
        const adPodInfo = ad.getAdPodInfo();
        this.adPodDuration = adPodInfo.getMaxDuration();
        this.elapsedAdsInPodTime = 0;
        //log.call(this,'onContentPauseRequested', 'adPodDuration:', this.adPodDuration);
        this.contentResumeCalled = false;
        //this.dispatcher.publish(PaidInterruptionEvents.AD_START, { duration: this.adPodDuration });
        this.pluginSubject.next({
            type: AdvertisingPluginEvents.AD_START,
            duration: this.adPodDuration,
            player: this.mediaPlayer,
        });

    }

    /**
     *
     * @access private
     * @param {Object} adEvent
     * @desc event handler for the ad is complete and the content should resume. AD_COMPLETE is fired
     *
     */
    onContentResumeRequested(adEvent) {

        //log.call(this, 'onContentResumeRequested');
        if (!this.adBlockedError) {

            //this.dispatcher.publish(PaidInterruptionEvents.AD_COMPLETE, adEvent);
            this.pluginSubject.next({
                type: AdvertisingPluginEvents.AD_COMPLETE,
                player: this.mediaPlayer,
            });

        }

    }

    /**
     *
     * @accesss private
     * @desc dispatch AD_TIME_UPDATE_ELAPSED with the elapsed time of the ad(s)
     */
    updateRemainingTimer() {

        const remainingTime = this.adsManager.getRemainingTime();
        const duration = Math.max(this.currentAdDuration, remainingTime);
        const elapsedTime = duration - remainingTime;
        const podElapsedTime = this.elapsedAdsInPodTime + elapsedTime;

        const payload = {
            countdown: remainingTime,
            currentTime: podElapsedTime,    // not using this, but not sure if anything else is relying on it
            currentAdElapsedTime: elapsedTime,
            currentPodElapsedTime: podElapsedTime,
            currentAdDuration: duration,
        };

        // Send AD_TIME_UPDATE_ELAPSED message
        this.pluginSubject.next({
            type: AdvertisingPluginEvents.AD_TIME_UPDATE_ELAPSED,
            ...payload,
        });

    }

    trackMoat() {

        var moatTrackingEnabled = _get(this.config, 'dfp.moatTrackingEnabled', false);
        var moatPartnerCode = _get(this.config, 'dfp.moatPartnerCode', null);

        if (moatTrackingEnabled && moatPartnerCode) {

            var ids = {
                partnerCode: moatPartnerCode,
                viewMode: this.googleIMA.ViewMode.NORMAL,
            };

            MoatTracking.initMoatTracking(this.adsManager, ids, this.adContainer);

        }

    };

    /**
     * @access public
     * @param {Number} w - width
     * @param {Number} h - height
     * @param {Boolean} isFullScreen
     * @desc relay the new player size to the ad plugin
     * @memberof DFPPlugin
     *
     */
    setSize(w = 640, h = 360, isFullScreen = false) {

        this.width = w;
        this.height = h;
        this.isFullscreen = isFullScreen;
        _set(this.config, 'dfp.width', w);
        _set(this.config, 'dfp.height', h);
        if (this.adsManager) {

            //log.call(this, 'setSize', w, h, isFullScreen);
            this.adsManager.resize(w, h, this.googleIMA.ViewMode.NORMAL);


        }

    };

    /**
     *
     * @desc Clean up the DFPPlugin and associated objects.
     *
     */
    destroy() {

        clearTimeout(this.requestAdTimeoutID);

        if (this.adsManager) {

            this.adsManager.destroy();

        }

    }

    /**
     *
     * @desc Utility function for returning the name of the class.
     * @return string The class type.
     *
     */
    toString() {

        return 'DfpPlugin';

    }

}

export default DfpPlugin;
